import { useEffect, useMemo, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';

import type { Resource } from '@setvi/shared/interfaces';
import { phoneNumberNoLetters, validEmail } from '@setvi/shared/utils';

import { useSubscribedMutation } from 'Hooks/React-Query';

import {
  useCoverTemplates,
  generateDynamicCoverPageMutation
} from '../../../api/cover-templates';
import type {
  CoverDefaults,
  CoverFieldKey,
  CoverFieldValues,
  CoverForm,
  PrintOrderItemCustomization
} from '../../../types';
import { COVER_FIELDS, createEmptyCoverForm } from '../constants';

// The rep's stored profile details fill the empty, untouched fields. Fields
// they already edited are left alone, so a cleared line stays cleared.
const applyDefaults = (
  form: CoverForm,
  defaults: CoverDefaults | undefined
): CoverForm => ({
  ...form,
  values: COVER_FIELDS.reduce(
    (acc, field) => ({
      ...acc,
      [field.key]: form.touched.includes(field.key)
        ? form.values[field.key]
        : form.values[field.key] || defaults?.[field.key] || ''
    }),
    {} as CoverFieldValues
  )
});

interface UseCoverSectionArgs {
  resource: Resource;
  initial?: PrintOrderItemCustomization;
  /** False for assets that take no cover page; skips the cover-page fetch. */
  enabled: boolean;
}

/**
 * The cover page: whether the rep wants one at all (the toggle), which one is
 * selected, the details stamped onto it, and the generated PDF.
 *
 * Picking a cover generates it from the details as they are at that moment, and
 * picking it again regenerates it. Changing a detail drops the generated cover
 * rather than leaving a preview that no longer matches, so the rep picks the
 * cover again to refresh. Turning the toggle off puts everything back to the
 * initial state.
 */
export const useCoverSection = ({
  resource,
  initial,
  enabled
}: UseCoverSectionArgs) => {
  const { enqueueSnackbar } = useSnackbar();

  // Off by default; on when editing an item that already carries a cover.
  const [active, setActive] = useState(!!initial?.addCover);
  const [templateId, setTemplateId] = useState(
    initial?.coverTemplateId ?? null
  );
  const [form, setForm] = useState<CoverForm>(
    () => initial?.coverFields ?? createEmptyCoverForm()
  );
  // The generated cover PDF: url drives the preview, filename is sent on sync.
  // A cover restored from the cart already has both, so it is not regenerated
  // just because the dialog opened.
  const [previewUrl, setPreviewUrl] = useState(initial?.coverPreviewUrl);
  const [fileName, setFileName] = useState(initial?.coverFileName);
  // Pages in the generated PDF (cover + the asset's own pages).
  const [pageCount, setPageCount] = useState(1);

  const { mutateAsync: generateCover, isLoading: generating } =
    useSubscribedMutation(generateDynamicCoverPageMutation());

  const {
    templates,
    defaults,
    available,
    message,
    loading: templatesLoading
  } = useCoverTemplates(resource.ID, enabled);

  // Pre-fill from the rep's stored profile details once the GET resolves.
  const prefilledRef = useRef(false);

  useEffect(() => {
    if (prefilledRef.current || !defaults) return;
    prefilledRef.current = true;
    setForm(prev => applyDefaults(prev, defaults));
  }, [defaults]);

  // Both details are optional, but a filled one has to be usable.
  const errors = useMemo<Partial<Record<CoverFieldKey, string>>>(() => {
    const found: Partial<Record<CoverFieldKey, string>> = {};
    const phone = form.values.PhoneNumber.trim();
    const email = form.values.EmailAddress.trim();

    if (phone && !phoneNumberNoLetters.test(phone))
      found.PhoneNumber = 'Enter a phone number without letters.';
    if (email && !validEmail.test(email))
      found.EmailAddress = 'Enter a valid email address.';

    return found;
  }, [form.values]);

  const hasErrors = Object.keys(errors).length > 0;

  // At least one detail typed, or a logo of the rep's own. The company logo
  // shown by default does not count — it is not something they filled in.
  const hasAnyField =
    COVER_FIELDS.some(field => !!form.values[field.key].trim()) ||
    !!form.logoBase64;

  // Drop the generated cover. The selection and details stay, so a regenerate
  // is enough.
  const clearGenerated = () => {
    setPreviewUrl(undefined);
    setFileName(undefined);
    setPageCount(1);
  };

  // Any edit invalidates a generated cover, so the rep regenerates it.
  // `touched` records the edit: a cleared field is sent as '' and prints blank,
  // instead of falling back to the profile value.
  const editForm = (change: (prev: CoverForm) => CoverForm) => {
    setForm(change);
    clearGenerated();
  };

  return {
    /** The toggle: whether the rep wants a cover page at all. */
    active,
    templateId,
    form,
    errors,
    hasErrors,
    hasAnyField,
    previewUrl,
    fileName,
    pageCount,
    templates,
    templatesLoading,
    available,
    message,
    generating,
    /** A cover is picked but its PDF has not been generated (or was dropped). */
    isPending: !!templateId && !previewUrl,
    /** The company logo, shown until the rep uploads their own. */
    defaultLogoUrl: defaults?.LogoUrl ?? null,

    /**
     * Pick a cover and generate it from the details as they are right now;
     * picking the selected one again regenerates it. Resolves true when a PDF
     * came back.
     */
    select: async (id: string): Promise<boolean> => {
      // Never generate a cover from details that would be stamped wrong.
      if (hasErrors || generating) return false;

      setTemplateId(id);
      clearGenerated();
      try {
        const data = await generateCover({
          resourceId: resource.ID,
          coverPageName: id,
          fields: form
        });
        setPreviewUrl(data?.DownloadURLWithSas ?? undefined);
        setFileName(data?.FileName ?? undefined);
        setPageCount(data?.PageCount ?? 1);
        return true;
      } catch {
        // Error toast handled globally by the mutation cache.
        return false;
      }
    },

    setField: (key: CoverFieldKey, value: string) =>
      editForm(prev => ({
        ...prev,
        values: { ...prev.values, [key]: value },
        touched: prev.touched.includes(key)
          ? prev.touched
          : [...prev.touched, key]
      })),

    /**
     * Replacement logo. The generator takes bytes rather than a url, so the
     * picked file is read as a data url; `null` removes the logo entirely.
     */
    setLogo: (file: File | null) => {
      if (!file) {
        editForm(prev => ({ ...prev, logoBase64: '' }));
        return;
      }

      const reader = new FileReader();
      reader.onload = () =>
        editForm(prev => ({ ...prev, logoBase64: String(reader.result) }));
      reader.onerror = () =>
        enqueueSnackbar('Could not read that image.', { variant: 'error' });
      reader.readAsDataURL(file);
    },

    /** Back to the company logo, undoing an upload or a removal. */
    resetLogo: () => editForm(prev => ({ ...prev, logoBase64: undefined })),

    /** Drop the cover entirely, selection included. */
    clear: () => {
      setTemplateId(null);
      clearGenerated();
    },

    /**
     * Turn the cover page on or off. Off returns to the initial state: no
     * selection, no generated cover, and the details back to the profile
     * defaults.
     */
    setActive: (next: boolean) => {
      setActive(next);
      if (next) return;
      setTemplateId(null);
      clearGenerated();
      setForm(applyDefaults(createEmptyCoverForm(), defaults));
    }
  };
};
